import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { find, map, Observable } from 'rxjs';
import { Routes } from '../../../environments/vars/routes';
import { ApplicationField } from '../models/application-field';
import { ClientApplication } from '../models/client-application';
import { ApplicationSection } from '../models/application-section';
import { ClientApplicationField } from '../models/client-application-field';
import { CreateApplicationRequest } from '../models/requests/create-application-request';
import { CreateApplicationResponseRequest } from '../models/requests/create-application-response-request';
import {
    ApplicationResponseDisplay,
    ApplicationResponseItem,
    CompletedApplication,
} from '../models/completed-application';
import { FormGroup } from '@angular/forms';
import { QuestionBase } from '../models/application-questions/question-base';
import { QuestionControlService } from './question-control.service';
import { ClientApplicationFormSection } from '../models/client-application-form-section';
import { FormField } from '../models/form-field';

@Injectable({
    providedIn: 'root',
})
export class ClientApplicationService {
    constructor(
        private questionService: QuestionControlService,
        private _router: Router,
        private _http: HttpClient,
    ) {}

    public getClientApplicationForm(
        clientId: string,
    ): Observable<ClientApplication> {
        return this._http
            .get<{
                data: { fields: any };
            }>(Routes.RFApi.ClientApplication(clientId))
            .pipe(
                map((res) => {
                    return new ClientApplication(res.data);
                }),
            );
    }

    public getApplicationFormToFillOut(
        applicationId: string,
    ): Observable<ClientApplication> {
        return this._http
            .get<{
                data: { fields: any };
            }>(Routes.RFApi.Application(applicationId))
            .pipe(
                map((res) => {
                    return new ClientApplication(res.data);
                }),
            );
    }

    public getApplicationFields(): Observable<ApplicationField[]> {
        return this._http
            .get<{ data: ApplicationField[] }>(Routes.RFApi.ApplicationFields)
            .pipe(
                map((res) => {
                    return res.data.map((r) => new ApplicationField(r));
                }),
            );
    }

    public createApplication(
        clientId: string,
        request: CreateApplicationRequest[],
    ): Observable<void> {
        return this._http.post<void>(
            Routes.RFApi.ClientApplication(clientId),
            JSON.stringify({ fields: request }),
        );
    }

    public saveApplicationResponses(
        applicationId: string,
        request: CreateApplicationResponseRequest,
    ): Observable<CompletedApplication> {
        return this._http
            .post<{
                data: CompletedApplication;
            }>(
                Routes.RFApi.Application(applicationId) + '/response',
                JSON.stringify(request),
            )
            .pipe(
                map((res) => {
                    return new CompletedApplication(res.data);
                }),
            );
    }

    public getApplicationResponses(
        applicationId: string,
        contactId: string,
    ): Observable<CompletedApplication> {
        return this._http
            .get<{
                data: CompletedApplication;
            }>(
                Routes.RFApi.ApplicationResponses(applicationId) +
                    `?contactId=${contactId}`,
            )
            .pipe(
                map((res) => {
                    return new CompletedApplication(res.data);
                }),
            );
    }

    /** Helper methods */

    public mapWorkingApplicationFieldsToList(
        allFields: ApplicationField[] = [],
        clientApplicationFields: ClientApplicationField[] = [],
    ): ApplicationSection[] {
        if (!allFields.length || !clientApplicationFields.length) {
            return [];
        }

        const sections = [
            ...new Set(allFields.flatMap((field) => field.applicationSection)),
        ].map((section) => {
            return <ApplicationSection>{
                section: section,
                fields: allFields
                    .filter(
                        (field) =>
                            field.applicationSection === section &&
                            !field.dependsOn,
                    )
                    .map((field) => {
                        return {
                            ...field,
                            isSelected: !!clientApplicationFields.find(
                                (caf) => caf.applicationFieldId === field.id,
                            ),
                            childField: allFields.find(
                                (f: ApplicationField) =>
                                    f.dependsOn === field.id,
                            ),
                        };
                    }),
            };
        });

        /** Sort the fields in each section; selected have real sort order; others just use default sort */
        sections.forEach((section) => {
            section.fields = [
                ...section.fields
                    .filter((f) => f.isSelected)
                    // @ts-ignore
                    .sort((a: ApplicationField, b: ApplicationField) => {
                        // @ts-ignore
                        const existingFieldA: ClientApplicationField =
                            clientApplicationFields.find(
                                (caf) => caf.applicationFieldId === a.id,
                            );
                        // @ts-ignore
                        const existingFieldB: ClientApplicationField =
                            clientApplicationFields.find(
                                (caf) => caf.applicationFieldId === b.id,
                            );
                        return (
                            existingFieldA.sortOrder - existingFieldB.sortOrder
                        );
                    }),
                ...section.fields
                    .filter((f) => !f.isSelected)
                    .sort((a, b) => a.defaultSort - b.defaultSort),
            ];
        });

        return sections;
    }

    /** Shared method to get the fields/questions for a section of an application form, ready to render as inputs on the form */
    public getFieldsForSection(
        application: ClientApplication,
        filteredFields: ClientApplicationField[],
    ) {
        return filteredFields
            .filter((field: ClientApplicationField) => !field.field.dependsOn)
            .map((field: ClientApplicationField) => {
                field.field.question.key = field.clientApplicationFieldId;

                const childField = application.fields.find(
                    (f: ClientApplicationField) =>
                        f.field.dependsOn === field.field.id,
                );

                if (childField) {
                    childField.field.question.key =
                        childField.clientApplicationFieldId;
                    field.field.question.childQuestion =
                        childField.field.question;
                }
                return {
                    ...field,
                    childField: childField,
                };
            });
    }

    /** Take a person's answers and put them into viewable form for the admin */
    public mapApplicationResponsesToApplicationFields(
        responses: ApplicationResponseItem[],
        fields: ClientApplicationField[],
    ): ApplicationResponseDisplay[] {
        const returnArray: ApplicationResponseDisplay[] = [];

        // figure out the highest number of any response set
        const responseSets = [
            ...new Set(responses.map((r) => r.responseSet)),
        ].sort((a: number, b: number) => a - b);
        const highestResponseSet = responseSets.pop() || 0;

        // loop on the return sets and only add the answers in that response set, each time
        for (let i = 1; i <= highestResponseSet; i++) {
            fields.forEach((field: ClientApplicationField) => {
                const findResponse = responses.filter(
                    (r) =>
                        r.clientApplicationFieldId ===
                            field.clientApplicationFieldId &&
                        r.responseSet === i,
                );
                if (findResponse.length) {
                    returnArray.push({
                        section: field.field.applicationSection,
                        question: field.field,
                        response: findResponse[0].response,
                        responseSet: findResponse[0].responseSet,
                    });
                }
            });
        }

        return returnArray;
    }

    /** Shared logic to get the form questions ready for display */
    public parseFormForDisplay(
        application: ClientApplication,
    ): ClientApplicationFormSection[] {
        const allForms: ClientApplicationFormSection[] = [];

        // array of unique section titles in this application
        const allSectionTitles: string[] = [
            ...new Set(
                application.fields.flatMap(
                    (field) => field.field.applicationSection,
                ),
            ),
        ];

        allSectionTitles.forEach((section: string) => {
            const filteredFields = application.fields.filter(
                (field: ClientApplicationField) =>
                    field.field.applicationSection === section,
            );

            // assemble the array of fields for iterating on the screen, as well as a flat map of questions to feed the form
            const fields: ClientApplicationField[] = this.getFieldsForSection(
                application,
                filteredFields,
            );
            const sectionCanStartWithNone = !!filteredFields.filter(
                (field: ClientApplicationField) => field.field.doAllowNone,
            ).length;

            // some sections (vehicles, add'l income) don't have to start with any form, and can remove all forms for that section
            let form!: FormGroup;
            if (!sectionCanStartWithNone) {
                const questions = fields.map((field) => field.field.question);
                form = this.questionService.toFormGroup(
                    questions as QuestionBase<string>[],
                );
            }

            // form field arrays starts out empty
            const obj: ClientApplicationFormSection = {
                title: section,
                formFieldArrays: sectionCanStartWithNone
                    ? []
                    : [
                          {
                              fields: fields,
                              form: form,
                          },
                      ],
                canHaveMultiple: !!filteredFields.filter(
                    (field: ClientApplicationField) =>
                        field.field.doAllowMultiple,
                ).length,
                canHaveNone: sectionCanStartWithNone,
            };

            allForms.push(obj);
        });

        return allForms;
    }

    /** Add one section to the application form */
    public addFormToFormArray(
        section: ClientApplicationFormSection,
        application: ClientApplication,
    ) {
        // assemble the array of fields for iterating on the screen, as well as a flat map of questions to feed the form
        let fields: ClientApplicationField[] = [];
        if (!section.formFieldArrays[0]) {
            const filteredFields = application.fields.filter(
                (field: ClientApplicationField) =>
                    field.field.applicationSection === section.title,
            );
            fields = this.getFieldsForSection(application, filteredFields);
        } else {
            fields = section.formFieldArrays[0].fields;
        }

        const questions = fields.map((field) => field.field.question);
        const form = this.questionService.toFormGroup(
            questions as QuestionBase<string>[],
        );

        const formFields: FormField = {
            fields: fields,
            form: form,
        };

        section.formFieldArrays.push(formFields);
    }
}
